import React, { useState, useEffect, useCallback } from "react";
import ApiService from "../services/ApiService";
import "./BookingManagement.css";

const BookingManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Modal state
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusNotes, setStatusNotes] = useState("");
  const [updating, setUpdating] = useState(false);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAllBookings(
        currentPage,
        pageSize,
        searchTerm,
        statusFilter,
        startDate,
        endDate
      );

      setBookings(response.data.bookings || []);
      setCurrentPage(response.data.pagination.currentPage);
      setTotalPages(response.data.pagination.totalPages);
      setTotalCount(response.data.pagination.totalCount);
      setError("");
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setError("Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, statusFilter, startDate, endDate]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchBookings();
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  const handleStatusUpdate = async () => {
    if (!selectedBooking || !newStatus) return;

    try {
      setUpdating(true);
      await ApiService.updateBookingStatus(
        selectedBooking.id,
        newStatus,
        statusNotes.trim() || null
      );

      setSuccess("Booking status updated successfully!");
      await fetchBookings();
      handleCloseStatusModal();
    } catch (error) {
      console.error("Error updating booking status:", error);
      setError(error.message || "Failed to update booking status");
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenStatusModal = (booking) => {
    setSelectedBooking(booking);
    setNewStatus(booking.status);
    setStatusNotes("");
    setShowStatusModal(true);
  };

  const handleCloseStatusModal = () => {
    setSelectedBooking(null);
    setNewStatus("");
    setStatusNotes("");
    setShowStatusModal(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    return timeString;
  };

  // Add scroll handler for table wrapper
  const handleTableScroll = (e) => {
    const wrapper = e.target;
    const scrollLeft = wrapper.scrollLeft;
    const maxScrollLeft = wrapper.scrollWidth - wrapper.clientWidth;

    // Remove all scroll classes
    wrapper.classList.remove(
      "scrolled-left",
      "scrolled-right",
      "scrolled-middle"
    );

    if (scrollLeft === 0) {
      // At the start
      wrapper.classList.add("scrolled-left");
    } else if (scrollLeft >= maxScrollLeft - 1) {
      // At the end (with 1px tolerance)
      wrapper.classList.add("scrolled-right");
    } else {
      // In the middle
      wrapper.classList.add("scrolled-middle");
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "confirmed":
        return "#10b981";
      case "completed":
        return "#059669";
      case "cancelled":
        return "#ef4444";
      case "pending":
        return "#f59e0b";
      default:
        return "#6b7280";
    }
  };

  const getStatusOptions = () => [
    { value: "confirmed", label: "Confirmed" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
    { value: "pending", label: "Pending" },
  ];

  return (
    <div className="booking-management dark-theme">
      <div className="admin-header-section">
        <h1>Booking Management</h1>
        <p>View and manage all service bookings</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Filters Section */}
      <div className="booking-filters">
        <form onSubmit={handleSearch} className="filter-form">
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="search">Search</label>
              <input
                type="text"
                id="search"
                placeholder="Search by user name, email, or car model..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">All Statuses</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="startDate">Start Date</label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="endDate">End Date</label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="filter-input"
              />
            </div>
          </div>

          <div className="filter-actions">
            <button type="submit" className="search-btn">
              <span>🔍</span>
              Search
            </button>
            <button
              type="button"
              onClick={handleClearFilters}
              className="clear-btn"
            >
              <span>🗑️</span>
              Clear Filters
            </button>
          </div>
        </form>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <p>
          Showing {bookings.length} of {totalCount} bookings
          {statusFilter && ` (Status: ${statusFilter})`}
          {searchTerm && ` (Search: "${searchTerm}")`}
          {bookings.length > 0 && (
            <span className="scroll-hint">
              {" "}
              • Scroll horizontally to view all columns
            </span>
          )}
        </p>

        <div className="page-size-selector">
          <label htmlFor="pageSize">Items per page:</label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value));
              setCurrentPage(1);
            }}
            className="page-size-select"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Bookings Table */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading bookings...</p>
        </div>
      ) : (
        <div className="bookings-table-wrapper" onScroll={handleTableScroll}>
          {bookings.length === 0 ? (
            <div className="no-bookings-message">
              <h3>No bookings found</h3>
              <p>No bookings match your current filters.</p>
            </div>
          ) : (
            <table className="bookings-table">
              <thead>
                <tr>
                  <th>Booking ID</th>
                  <th>User Details</th>
                  <th>Residence Info</th>
                  <th>Date & Time</th>
                  <th>Service Type</th>
                  <th>Status</th>
                  <th>Credits Used</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking) => (
                  <tr key={booking.id} className="booking-row">
                    <td className="booking-id">
                      <span className="id-number">#{booking.id}</span>
                      <span className="created-date">
                        {formatDate(booking.createdAt)}
                      </span>
                    </td>
                    <td className="user-details">
                      <div className="user-name">
                        {booking.user?.displayName || "N/A"}
                      </div>
                      <div className="user-email">
                        {booking.user?.email || "N/A"}
                      </div>{" "}
                      <div className="user-car">
                        {booking.user?.carNumber
                          ? `${booking.user.carNumber} (${
                              booking.user?.carModel || "N/A"
                            })`
                          : booking.user?.carModel || "N/A"}
                      </div>
                    </td>
                    <td className="residence-info">
                      <div className="community-name">
                        {booking.user?.flat?.block?.residency?.name || "N/A"}
                      </div>
                      <div className="block-name">
                        Block: {booking.user?.flat?.block?.name || "N/A"}
                      </div>
                      <div className="flat-number">
                        Flat: {booking.user?.flat?.name || "N/A"}
                      </div>
                    </td>

                    <td className="booking-datetime">
                      <div className="booking-date">
                        {formatDate(booking.bookingDate)}
                      </div>
                      <div className="booking-time">
                        {booking.slot
                          ? `${formatTime(
                              booking.slot.startTime
                            )} - ${formatTime(booking.slot.endTime)}`
                          : "N/A"}
                      </div>
                    </td>
                    <td className="booking-service-type">
                      <span
                        className={`booking-service-badge ${booking.serviceType}`}
                      >
                        {booking.serviceType === "pay_per_use"
                          ? "Pay Per Use"
                          : "Regular"}
                      </span>
                    </td>

                    <td className="booking-status">
                      <span
                        className="status-badge"
                        style={{
                          backgroundColor: getStatusColor(booking.status),
                        }}
                      >
                        {booking.status}
                      </span>
                    </td>

                    <td className="credits-used">
                      <div className="credits-info">
                        {booking.creditsUsed > 0 && (
                          <span className="regular-credits">
                            Regular: {booking.creditsUsed}
                          </span>
                        )}
                        {booking.payPerUseCreditsUsed > 0 && (
                          <span className="ppu-credits">
                            Pay-per-use: {booking.payPerUseCreditsUsed}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="booking-actions">
                      <button
                        onClick={() => handleOpenStatusModal(booking)}
                        className="update-status-btn"
                        title="Update Status"
                      >
                        <span>✏️</span>
                        Update Status
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            ← Previous
          </button>

          <div className="pagination-info">
            <span>
              Page {currentPage} of {totalPages}
            </span>
          </div>

          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next →
          </button>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && selectedBooking && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Update Booking Status</h3>
              <button onClick={handleCloseStatusModal} className="close-btn">
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="booking-summary">
                <h4>Booking #{selectedBooking.id}</h4>
                <p>User: {selectedBooking.user?.displayName || "N/A"}</p>
                <p>Date: {formatDate(selectedBooking.bookingDate)}</p>
                <p>
                  Time:{" "}
                  {selectedBooking.slot
                    ? `${formatTime(
                        selectedBooking.slot.startTime
                      )} - ${formatTime(selectedBooking.slot.endTime)}`
                    : "N/A"}
                </p>
                <p>
                  Current Status:{" "}
                  <span className="current-status">
                    {selectedBooking.status}
                  </span>
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="newStatus">New Status</label>
                <select
                  id="newStatus"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="status-select"
                >
                  {getStatusOptions().map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="statusNotes">Notes (Optional)</label>
                <textarea
                  id="statusNotes"
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Add any notes about this status change..."
                  className="status-notes"
                  rows={3}
                />
              </div>
            </div>

            <div className="modal-actions">
              <button
                onClick={handleCloseStatusModal}
                className="cancel-btn"
                disabled={updating}
              >
                Cancel
              </button>
              <button
                onClick={handleStatusUpdate}
                disabled={updating || !newStatus}
                className="save-btn"
              >
                {updating ? "Updating..." : "Update Status"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingManagement;
